import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from './States.js';
import { useT } from '../i18n/index.js';

interface State {
  failed: boolean;
}

/**
 * The last line of defence in the UI.
 *
 * A render error used to take the whole Mini App down to a blank page, which
 * from inside Telegram looks like the app is broken rather than one screen
 * being unhappy. This keeps the frame, says so, and offers a reload.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console for a developer; never shown to the user.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <CrashScreen />;
  }
}

/**
 * The fallback, as a function component so it can be translated.
 *
 * A class component cannot call a hook, and a crash screen the reader cannot
 * read is barely better than a blank one — so the boundary renders this, and
 * the translator sits *above* the boundary in `App.tsx` for exactly this
 * reason. The provider does nothing that can throw, so putting it outside
 * costs no coverage.
 */
function CrashScreen() {
  const { t } = useT();
  return (
    <div className="splash">
      <div style={{ width: 'min(100%, 380px)', padding: 'var(--space-5)' }}>
        <ErrorState
          title={t('boot.crashTitle')}
          description={t('boot.crashBody')}
          onRetry={() => window.location.reload()}
          retryLabel={t('common.reload')}
        />
      </div>
    </div>
  );
}
